import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import colors from "./../styles/Colors";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Visibility, Delete } from "@mui/icons-material";
import { apiService } from "./../services/api/ApiService";
import LoadingSpinner from "./../components/common/LoadingSpinner";
import { SelectChangeEvent } from "@mui/material/Select";

interface Transcripcion {
  id: number;
  name: string;
  numberCase: string;
  videoId: number;
  userId: number;
  companyId: number;
  status: "COMPLETED" | "PROCESSING" | "ERROR" | "IN_PROGRESS" | "HIDDEN";
  updatedAt: string;
  createdAt: string;
}

interface ListTranscriptorProps {
  setActiveItem: (item: string) => void;
}

type Order = "asc" | "desc";

const ListTranscriptor: React.FC<ListTranscriptorProps> = ({ setActiveItem }) => {
  const [transcripciones, setTranscripciones] = useState<Transcripcion[]>([]);
  const [filteredTranscriptions, setFilteredTranscriptions] = useState<Transcripcion[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [transcripcionToDelete, setTranscripcionToDelete] = useState<Transcripcion | null>(null);
  const [confirmName, setConfirmName] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 🚫 sin sort por defecto
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof Transcripcion | null>(null);

  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTranscriptions = async () => {
      try {
        setLoading(true);
        const data = await apiService.getTranscriptions();

        setTranscripciones(data);
        const filtered = data.filter((t) => t.status !== "HIDDEN");
        setFilteredTranscriptions(filtered);

        const hasInProgress = data.some((t) => t.status === "IN_PROGRESS");
        setAutoRefresh(hasInProgress);
      } catch (err: unknown) {
        if (typeof err === "object" && err !== null && "response" in err) {
          const error = err as { response?: { status?: number } };
          if (error.response?.status === 401) {
            setError("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.");
            window.location.href = "/";
            return;
          }
        }
        setError("Error al intentar obtener las transcripciones.");
      } finally {
        setLoading(false);
      }
    };

    fetchTranscriptions();
  }, []);

  // Auto-refresh solo si hay IN_PROGRESS
  useEffect(() => {
    if (autoRefresh) {
      const intervalId = setInterval(async () => {
        try {
          const data = await apiService.getTranscriptions();
          setTranscripciones(data);
          const filtered = data.filter((t) => t.status !== "HIDDEN");
          setFilteredTranscriptions(filtered);
        } catch (err) {
          setError("Error al obtener las transcripciones.");
        }
      }, 30000);

      return () => clearInterval(intervalId);
    }
  }, [autoRefresh]);

  // 👉 Derivar filas a renderizar: si no hay sort activo, respeta el orden del backend tal como vino.
  const rowsToRender = useMemo(() => {
    if (!orderBy) return filteredTranscriptions;

    const sorted = [...filteredTranscriptions].sort((a, b) => {
      let cmp = 0;

      if (orderBy === "createdAt") {
        const va = new Date(a.createdAt).getTime();
        const vb = new Date(b.createdAt).getTime();
        cmp = va - vb;
      } else if (orderBy === "name") {
        const va = (a.name ?? "").toLowerCase();
        const vb = (b.name ?? "").toLowerCase();
        cmp = va < vb ? -1 : va > vb ? 1 : 0;
      } else if (orderBy === "numberCase") {
        const va = (a.numberCase ?? "").toLowerCase();
        const vb = (b.numberCase ?? "").toLowerCase();
        cmp = va < vb ? -1 : va > vb ? 1 : 0;
      } else if (orderBy === "status") {
        // Ordenar por label legible
        const la = getStatusColor(a.status).label.toLowerCase();
        const lb = getStatusColor(b.status).label.toLowerCase();
        cmp = la < lb ? -1 : la > lb ? 1 : 0;
      } else {
        // Para otras columnas, no cambiar
        cmp = 0;
      }

      return order === "asc" ? cmp : -cmp;
    });

    return sorted;
  }, [filteredTranscriptions, orderBy, order]);

  const pageRows = rowsToRender.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleRequestSort = (property: keyof Transcripcion) => {
    if (orderBy === property) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrder("asc");
      setOrderBy(property);
    }
  };

  const handleChangePage = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PROCESSING":
        return { background: "#FFE082", color: "#FF8F00", label: "En proceso" };
      case "ERROR":
        return { background: "#FFCDD2", color: "#D32F2F", label: "Error" };
      case "COMPLETED":
        return { background: "#C6F6D5", color: "#2e7d32", label: "Completado" };
      case "IN_PROGRESS":
        return { background: "#BBDEFB", color: "#1976d2", label: "En progreso" };
      case "HIDDEN":
        return { background: "#E0E0E0", color: "#757575", label: "Oculto" };
      default:
        return { background: "#F5F5F5", color: "#9E9E9E", label: "Desconocido" };
    }
  };

  const handleOpenDeleteModal = (transcripcion: Transcripcion) => {
    setTranscripcionToDelete(transcripcion);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setTranscripcionToDelete(null);
    setConfirmName("");
  };

  const handleConfirmDelete = async () => {
    if (transcripcionToDelete && confirmName === transcripcionToDelete.name) {
      try {
        const updatedTranscription = {
          name: transcripcionToDelete.name,
          numberCase: transcripcionToDelete.numberCase,
        // @ts-ignore
          status: "HIDDEN",
        };

        await apiService.updateTranscriptionById(transcripcionToDelete.id, updatedTranscription);

        setTranscripciones((prev) =>
          prev.map((t) => (t.id === transcripcionToDelete.id ? { ...t, status: "HIDDEN" } : t))
        );
        setFilteredTranscriptions((prev) => prev.filter((t) => t.id !== transcripcionToDelete.id));

        handleCloseDeleteModal();
      } catch (err: unknown) {
        if (typeof err === "object" && err !== null && "response" in err) {
          const error = err as { response?: { status?: number } };
          if (error.response?.status === 401) {
            setError("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.");
            window.location.href = "/";
            return;
          }
        }
        setError("Error al intentar eliminar la transcripción.");
      }
    } else {
      alert("El nombre de la transcripción no coincide.");
    }
  };

  const handleViewDetails = (transcripcion: Transcripcion) => {
    if (transcripcion.status !== "PROCESSING") {
      navigate(`/transcriptor/${transcripcion.id}`);
    }
  };

  if (loading) return <LoadingSpinner message="Cargando transcripciones..." />;
  if (error) return <Typography color="error">{error}</Typography>;

  const totalPages = Math.ceil(rowsToRender.length / rowsPerPage);
  const currentTranscriptionStart = (page - 1) * rowsPerPage + 1;
  const currentTranscriptionEnd = Math.min(currentTranscriptionStart + rowsPerPage - 1, rowsToRender.length);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="textSecondary">
        Transcriptor/Mis transcripciones
      </Typography>

      <Typography
        variant="h4"
        sx={{
          fontFamily: "Montserrat",
          fontSize: "40px",
          fontWeight: 600,
          lineHeight: "48.76px",
          textAlign: "left",
        }}
        gutterBottom
      >
        Mis transcripciones
      </Typography>

      <Typography variant="body2" color="textSecondary" paragraph>
        En esta sección, podrás ver todas las transcripciones que has realizado. Utiliza la barra de búsqueda para
        encontrar transcripciones por nombre, número de expediente u otros criterios. Para visualizar una transcripción,
        haz clic en el ícono del ojo. Si deseas eliminar alguna, simplemente haz clic en el ícono de la papelera.
      </Typography>

      <TextField
        fullWidth
        placeholder="Buscar transcripciones..."
        variant="outlined"
        margin="normal"
        onChange={(e) => {
          const searchValue = e.target.value.toLowerCase();
          const filtered = transcripciones.filter(
            (t) =>
              t.name.toLowerCase().includes(searchValue) ||
              t.numberCase.toLowerCase().includes(searchValue)
          );
          setFilteredTranscriptions(filtered);
          setPage(1);
        }}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "createdAt" ? order : false}>
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? order : "desc"}
                  onClick={() => handleRequestSort("createdAt")}
                  hideSortIcon  // para que no muestre el icono si no está activo
                >
                  Fecha
                </TableSortLabel>
              </TableCell>

              <TableCell sortDirection={orderBy === "name" ? order : false}>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={() => handleRequestSort("name")}
                  hideSortIcon
                >
                  Nombre
                </TableSortLabel>
              </TableCell>

              <TableCell sortDirection={orderBy === "numberCase" ? order : false}>
                <TableSortLabel
                  active={orderBy === "numberCase"}
                  direction={orderBy === "numberCase" ? order : "asc"}
                  onClick={() => handleRequestSort("numberCase")}
                  hideSortIcon
                >
                  Número de Expediente
                </TableSortLabel>
              </TableCell>

              <TableCell sortDirection={orderBy === "status" ? order : false}>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={() => handleRequestSort("status")}
                  hideSortIcon
                >
                  Estado
                </TableSortLabel>
              </TableCell>

              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {pageRows.map((t) => (
              <TableRow key={t.id}>
                <TableCell>{moment(t.createdAt).format("DD/MM/YYYY HH:mm")}</TableCell>
                <TableCell>{t.name}</TableCell>
                <TableCell>{t.numberCase}</TableCell>
                <TableCell>
                  <Chip
                    label={getStatusColor(t.status).label}
                    sx={{
                      backgroundColor: getStatusColor(t.status).background,
                      color: getStatusColor(t.status).color,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleViewDetails(t)} sx={{ color: "black" }}>
                    <Visibility />
                  </IconButton>
                  <IconButton onClick={() => handleOpenDeleteModal(t)} sx={{ color: "red" }}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
        <FormControl variant="outlined" size="small">
          <InputLabel>Transcripciones por página</InputLabel>
          <Select value={rowsPerPage} onChange={handleChangeRowsPerPage} label="Transcripciones por página">
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
          </Select>
        </FormControl>

        <Typography color="textSecondary">
          {rowsToRender.length === 0
            ? "0 de 0 transcripciones"
            : `${currentTranscriptionStart}-${currentTranscriptionEnd} de ${rowsToRender.length} transcripciones`}
        </Typography>

        <Box>
          <IconButton onClick={() => setPage(page > 1 ? page - 1 : 1)}>
            <i className="fas fa-chevron-left"></i>
          </IconButton>
          <Typography color="textSecondary">
            {page} de {Math.max(totalPages, 1)} páginas
          </Typography>
          <IconButton onClick={() => setPage(page < totalPages ? page + 1 : totalPages)}>
            <i className="fas fa-chevron-right"></i>
          </IconButton>
        </Box>
      </Box>

      {/* Modal de confirmación de eliminación */}
      <Dialog open={openDeleteModal} onClose={handleCloseDeleteModal}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar la transcripción "{transcripcionToDelete?.name}"? Escribe el nombre para
            confirmar.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre de la transcripción"
            fullWidth
            variant="outlined"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="primary">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListTranscriptor;
